// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import {
	EntityLoanPayload,
	ErrorCause,
	ExchangeRateByDate,
	Financier,
	LoanConversionPayload,
	LoanPayload,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';

// mui
import { Alert, Grid, InputAdornment, SelectChangeEvent } from '@mui/material';

// fields
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { Input } from '../../fields/Input/Input';
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';
import { SelectCurrency } from '../../fields/SelectCurrency/SelectCurrency';
import { AmountsConversions } from '../../fields/AmountsConversions/AmountsConversions';

// icons
import {
	AccountBalanceOutlined as IconAccountBalance,
	PublicOutlined as IconPublic,
} from '@mui/icons-material';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface FormAddFinancingSourceProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddFinancingSource = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddFinancingSourceProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { userEntity } = useContext(ContextUserEntity);

	// hooks
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	const defaultValues = {
		amount: 0,
		currency: '',
		dateApproved: '',
		dateClosing: '',
		dateSigning: '',
		financier: 'GOV',
		name: '',
		num: '',
	};

	// state
	const [error, setError] = useState<string | null>(null);
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>([]);
	const [values, setValues] = useState(defaultValues);

	// vars
	const idForm = `form-add-loan`;

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	const handleChangeCurrency = (e: SelectChangeEvent<unknown>) => {
		const currency = String(e.target.value);

		setValues({
			...values,
			currency,
		});

		if (currency) {
			const currenciesForExchangeRates = getCurrenciesForExchangeRates({ currency });
			const exchangeRatesNew: ExchangeRateByDate[] = currenciesForExchangeRates.map((curr) => ({
				currency: curr,
				dateExchangeRate: '',
				exchangeRate: 0,
			}));

			setExchangeRates(exchangeRatesNew);
		} else {
			setExchangeRates([]);
		}
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());
		const id = `${entries.financier}${entries.num}`;

		try {
			// set loader
			setIsLoading(true);

			const entitiesNew: EntityLoanPayload[] = [];

			if (userEntity?.id) {
				entitiesNew.push({
					id: userEntity.id,
					parent: null,
				});
			}

			// Define conversions using exchange rates
			const conversions: LoanConversionPayload[] = exchangeRates.map((exchangeRate) => {
				// Get the converted amount from the form entries (already calculated by AmountsConversions)
				const conversionFieldName = `amount-conversion-${exchangeRate.currency}`;
				const convertedAmount = entries[conversionFieldName]
					? Number(entries[conversionFieldName])
					: 0;

				return {
					amount: convertedAmount,
					currency: exchangeRate.currency,
					dateExchangeRate: exchangeRate.dateExchangeRate,
					exchangeRate: exchangeRate.exchangeRate,
					tax: 0,
					type: 'AMOUNT',
				};
			});

			const body: LoanPayload = {
				accounts: [],
				amount: entries.amount ? Number(entries.amount) : null,
				conversions,
				currency: String(entries.currency),
				dateApproved: entries.dateApproved ? String(entries.dateApproved) : null,
				dateClosing: entries.dateClosing ? String(entries.dateClosing) : null,
				disbursed: null,
				entities: entitiesNew,
				financier: entries.financier as Financier,
				name: entries.name ? String(entries.name) : null,
				num: String(entries.num),
				project: project?.id,
				status: 'ENABLED',
			};

			const fetchAddFinancingSource = await fetch(`${process.env.REACT_APP_API_URL}/loans`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddFinancingSource = await fetchAddFinancingSource.json();

			if (resAddFinancingSource.error) {
				throw new Error(resAddFinancingSource.error.message, {
					cause: {
						id: resAddFinancingSource.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.financingSourceCreated', { id }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'LOAN_DUPLICATE') {
					message = t('error.loanDuplicate', { financier: entries.financier, num: entries.num });
				}
			}

			// set error
			setError(message);
		} finally {
			// set loader
			setIsLoading(false);
		}
	};

	return (
		<form id={idForm} name="formAddFinancingSource" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<RadioButtons
						inputs={[
							{
								icon: <IconAccountBalance />,
								label: t('government'),
								value: 'GOV',
							},
							{
								icon: <IconPublic />,
								label: t('other'),
								value: 'OTH',
							},
						]}
						id={`${idForm}-financier`}
						label={t('financier')}
						name="financier"
						onChange={(e, value) => {
							setValues({
								...values,
								financier: value,
							});
						}}
						required={true}
					/>
				</Grid>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-num`}
						inputProps={{
							maxLength: 5,
						}}
						InputProps={{
							startAdornment: <InputAdornment position="start">{values.financier}</InputAdornment>,
						}}
						label={t('number')}
						name="num"
						onChange={handleChangeValue}
						required={true}
						value={values.num}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<SelectCurrency
						id={`${idForm}-currency`}
						name="currency"
						onChange={handleChangeCurrency}
						required={true}
						value={values.currency}
					/>
				</Grid>
				{featureFlags['IFRS'] && exchangeRates.length > 0 && values.currency && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={values.currency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Grid item xs={12}>
						<AmountsConversions
							currency={values.currency}
							exchangeRates={exchangeRates}
							inputs={[
								{
									id: `${idForm}-amount`,
									name: 'amount',
									onChange: handleChangeValue,
									required: true,
									type: 'number',
									value: values.amount,
								},
							]}
							label={t('totalFinancedAmount')}
						/>
					</Grid>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-approved`}
						label={t('approvedDate')}
						name="dateApproved"
						onChange={handleChangeValue}
						type="date"
						value={values.dateApproved}
					/>
				</Grid>
				<Grid item xs={12} md={6}>
					<Input
						id={`${idForm}-date-closing`}
						label={t('closingDate')}
						name="dateClosing"
						onChange={handleChangeValue}
						type="date"
						value={values.dateClosing}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="info">{t('fundingReceivedInfo')}</Alert>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
